import React, { useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { Typography } from '@material-ui/core';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

type ListBoxComponentProps = {
  children: React.ReactNode;
};

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement>(function ListboxComponent(
  props: ListBoxComponentProps,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(() => itemSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const useStyles = makeStyles({
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

export const VirtualizedAutocomplete = ({ value, onChange, options, label, helperText }) => {
  const classes = useStyles();
  const [selected, setSelected] = useState(
    options.find(opt => opt.value === value) || { value: '', label: '' },
  );

  useEffect(() => {
    setSelected(options.find(opt => opt.value === value) || { value: '', label: '' });
  }, [value, options]);

  return (
    <Autocomplete
      style={{ width: 'calc(100% + 24px)' }}
      value={selected}
      onChange={(event: any, newValue: { value: string; label: string } | null) => {
        setSelected(newValue);
        onChange(newValue && newValue.value);
      }}
      disableListWrap
      classes={classes}
      ListboxComponent={ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
      options={options}
      getOptionLabel={option => option.label || ''}
      renderInput={params => (
        <TextField {...params} fullWidth variant="outlined" label={label} helperText={helperText} />
      )}
      renderOption={(option: any) => <Typography noWrap>{option.label}</Typography>}
    />
  );
};

export const FreeformAutocomplete = ({ value, onChange, options, label, required = false }) => {
  const filterOptions = opts => {
    return opts.filter(opt => {
      return opt.toLowerCase().indexOf(value.toLowerCase()) === 0;
    });
  };
  return (
    <Autocomplete
      freeSolo
      value={value}
      onInputChange={(event, newValue) => {
        onChange(newValue);
      }}
      filterOptions={filterOptions}
      options={options}
      renderInput={params => (
        <TextField {...params} fullWidth variant="outlined" label={label} required={required} />
      )}
    />
  );
};
